import { Injectable } from '@angular/core';

import { MessageType } from '@cds/push';
import { SlimScoreboardCommand } from '@cds/push/fixture-commands';
import { ScoreboardSlim } from '@cds/scoreboards/v1';
import { DetailedFixtureFactory, FixtureFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { OfferGroupingService } from '@frontend/sports/betting-offer/feature/offer-grouping';
import { ConnectionConfig } from '@frontend/sports/common/client-config-data-access';
import {
    BaseSubscription,
    CdsPushService,
    CdsSubscription,
    DynamicCategorySubscription,
    FixtureAllSubscription,
    NonGridableSubscription,
    OutrightsSubscription,
    SpecialsSubscription,
    SubscriptionExtras,
} from '@frontend/sports/content-distribution/feature';
import {
    EventSubscription,
    EventSubscriptionRequest,
    MessageCallback,
    MessageHandler,
} from '@frontend/sports/event-subscription/feature/base-subscription';
import { DetailedEventSubscriptionService } from '@frontend/sports/event-subscription/feature/detailed-event-subscription';
import { SubscriptionTopic } from '@frontend/sports/grid/core/feature/model';
import { assign } from 'lodash-es';

class SubscriptionEventModel extends EventModel {
    constructor(
        event: EventModel,
        public topic?: SubscriptionTopic,
        public topicDetail?: string,
    ) {
        super();

        assign(this, event);
    }
}

/**
 * This service should be used for subscribing grid event for push. It automatically
 * subscribes to scoreboard slim and all grid configured markets including V1 and V2
 * games and option markets
 *
 * @export
 * @class GridSubscriptionService
 * @extends {BaseSubscriptionService}
 */
@Injectable({ providedIn: 'root' })
export class DetailedGridSubscriptionService extends DetailedEventSubscriptionService {
    constructor(
        private baseFactory: FixtureFactory,
        detailedFactory: DetailedFixtureFactory,
        push: CdsPushService,
        connection: ConnectionConfig,
        offerGroupingService: OfferGroupingService,
    ) {
        super(detailedFactory, push, connection, offerGroupingService);
    }

    override subscribe(
        request: EventSubscriptionRequest<EventModel>[],
        topic?: SubscriptionTopic,
        topicDetail?: string,
    ): EventSubscription<EventModel> {
        return super.subscribe(
            request.map((current) => ({ event: new SubscriptionEventModel(current.event, topic, topicDetail), callback: current.callback })),
        );
    }

    protected override getHandlers(): Map<MessageType, MessageHandler<EventModel>> {
        const handlers = super.getHandlers();

        handlers.set(MessageType.ScoreboardSlim, (evt, cmd) => this.updateScoreboard(evt, cmd as SlimScoreboardCommand));

        return handlers;
    }

    protected override getContextSubscriptions(event: SubscriptionEventModel, callback: MessageCallback): CdsSubscription[] {
        switch (event.topic) {
            case SubscriptionTopic.Outrights:
                return this.get(OutrightsSubscription, event, callback);
            case SubscriptionTopic.Specials:
                return this.get(SpecialsSubscription, event, callback);
            case SubscriptionTopic.NonGridable:
                return this.get(NonGridableSubscription, event, callback);
            case SubscriptionTopic.All:
                return this.get(FixtureAllSubscription, event, callback);
            case SubscriptionTopic.DynamicCategory:
                if (!event.topicDetail) {
                    throw new Error(`DynamicCategorySubscription requires topicDetail to be set. EventId: ${event.id}, Topic: ${event.topic}`);
                }
                return this.get(DynamicCategorySubscription, event, callback, { suffix: event.topicDetail });
            default:
                throw new Error('Not supported');
        }
    }

    private get<T extends BaseSubscription>(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        type: new (...args: any[]) => T,
        event: SubscriptionEventModel,
        callback: MessageCallback,
        extras: SubscriptionExtras = {},
    ): BaseSubscription[] {
        const subscriptions: BaseSubscription[] = [new type(event.offerContext, event.id, this.connection.culture, callback, extras)];

        if (event.winGameLinkedFixture) {
            subscriptions.push(
                new FixtureAllSubscription(event.winGameLinkedFixture.contexts, event.winGameLinkedFixture.id, this.connection.culture, callback),
            );
        }

        return subscriptions;
    }

    protected updateScoreboard(event: EventModel, command: SlimScoreboardCommand): EventModel {
        const scoreboard = command.scoreboard as ScoreboardSlim;
        this.baseFactory.updateScoreboard(event, scoreboard);

        return event;
    }
}
